import { DuplicateLayerCommand } from "../commands/DuplicateLayerCommand";
import { FloatSelectionCommand } from "../commands/FloatSelectionCommand";
import type { ExtraLayerOriginal } from "../commands/FloatSelectionCommand";
import { MoveLayerCommand } from "../commands/MoveLayerCommand";
import { MoveLayerAndSelectionCommand } from "../commands/MoveLayerAndSelectionCommand";
import type { ExtraLayerMove } from "../commands/MoveLayerAndSelectionCommand";
import { MoveLayersCommand } from "../commands/MoveLayersCommand";
import type { LayerMoveEntry } from "../commands/MoveLayersCommand";
import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import { findLayer } from "../document/LayerUtils";
import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

/** Pre-drag snapshot of an "extra" layer moving alongside the primary one. */
interface ExtraLayerStart {
  readonly layerId: string;
  readonly startX: number;
  readonly startY: number;
}

/**
 * Gather the ids of every OTHER layer that should move together with
 * `primaryId` in this gesture: every other multi-selected layer in the
 * Layers panel (ctx.selectedLayerIds), plus any layer the primary declares
 * as linked (Layer.linkedLayerIds — read straight off the resolved layer
 * object rather than threading a duplicate set through ToolContext, since
 * it's already reachable via ctx.doc and staying there avoids a staleness
 * trap if auto-select reassigns the active layer earlier in the same
 * pointer-down). The primary's own id is always excluded (audit i4: "always
 * includes the active layer in practice, guard against double-applying").
 */
function collectExtraLayerIds(
  doc: Document,
  primaryId: string,
  selectedLayerIds: ReadonlySet<string>,
): string[] {
  const ids = new Set<string>();
  for (const id of selectedLayerIds) {
    if (id !== primaryId) ids.add(id);
  }
  const primary = findLayer(doc.layers, primaryId);
  const linked = primary?.linkedLayerIds;
  if (linked) {
    for (const id of linked) {
      if (id !== primaryId) ids.add(id);
    }
  }
  return [...ids];
}

/**
 * Resolve extra layer ids to their current position, skipping ones that no
 * longer exist, are hidden, or are position-locked — mirroring the guard the
 * primary layer already gets in onPointerDown. Locked/missing extras simply
 * don't move; they don't block the rest of the gesture.
 */
function resolveExtraStarts(doc: Document, ids: readonly string[]): ExtraLayerStart[] {
  const out: ExtraLayerStart[] = [];
  for (const id of ids) {
    const l = findLayer(doc.layers, id);
    if (!l || !l.visible || l.lockAll || l.lockPosition) continue;
    out.push({ layerId: l.id, startX: l.x, startY: l.y });
  }
  return out;
}

export class MoveTool implements Tool {
  readonly id = "move";
  readonly shortcut = "V";

  private active: {
    layerId: string;
    startX: number;
    startY: number;
    grabDocX: number;
    grabDocY: number;
    lastDx: number;
    lastDy: number;
    /** Shift is held at drag start — constrain movement to H/V/45° axes. */
    shiftConstrain: boolean;
    /**
     * Axis frozen by the Shift constraint, chosen once from the dx/dy at the
     * moment Shift is first detected held during this drag, then reused for
     * every subsequent move (Photoshop locks the axis at that instant rather
     * than re-evaluating the dominant direction on every pointermove — i155).
     * "y" means dy is locked to zero (horizontal movement); "x" means dx is
     * locked to zero (vertical movement). Reset to null whenever Shift isn't
     * engaged, so the next Shift-hold picks a fresh axis.
     */
    lockedAxis: "x" | "y" | null;
    /** When set, this is a float-move: carry the selection so it marches with pixels. */
    selectionAtFloat: Selection | null;
    /**
     * Other selected/linked layers dragged alongside the primary (audit
     * i4/i56). Always empty for the alt-drag duplicate path — duplicating a
     * whole multi-selection is out of scope here (single-layer duplicate
     * behaviour is unchanged).
     */
    extraStarts: ExtraLayerStart[];
  } | null = null;

  private lastLayerId: string | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc?.activeLayerId) return;

    const autoSelect = ctx.autoSelect ?? false;
    const doAutoSelect = autoSelect || s.ctrlKey;

    if (doAutoSelect) {
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      const hit = findLayerAtPosition(ctx.doc, p.x, p.y);
      if (hit) {
        // i55: Auto-Select "Layer"/"Group" mode. ToolUX's Move options bar
        // writes the choice onto `state.autoSelectMode`, which toolCtx()
        // forwards onto ToolContext.autoSelectMode. Default to "layer" (today's
        // behaviour) when absent, matching existing callers/mocks.
        const autoSelectMode = ctx.autoSelectMode ?? "layer";
        const targetId = autoSelectMode === "group"
          ? findTopLevelAncestorId(ctx.doc.layers, hit.id) ?? hit.id
          : hit.id;
        const prevId = ctx.doc.activeLayerId;
        this.lastLayerId = targetId;
        ctx.emit({
          kind: "set-active-layer",
          apply(doc: Document) { return { ...doc, activeLayerId: targetId }; },
          invert(before: Document, _after: Document) {
            return {
              kind: "set-active-layer",
              apply(d: Document) { return { ...d, activeLayerId: prevId }; },
              invert() { return this; },
            };
          },
        });
      }
    }

    const layerId = this.lastLayerId ?? ctx.doc.activeLayerId;
    // Use findLayer (not findRaster) so type/shape/group/fill/smart-object layers
    // are all movable — matching Photoshop behaviour (#216).
    const layer = findLayer(ctx.doc.layers, layerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockPosition) return;

    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Other layers moving together with this one — multi-selection (i4) and/or
    // linked layers (i56). Duplicate-drag (alt) intentionally stays single-layer.
    const extraIds = collectExtraLayerIds(ctx.doc, layer.id, ctx.selectedLayerIds);
    const extraStarts = resolveExtraStarts(ctx.doc, extraIds);

    if (s.altKey) {
      // Duplicate-move works for ANY layer kind — Photoshop Alt+drag duplicates
      // raster, type, shape, group, adjustment, etc. layers alike (i53). Use the
      // kind-agnostic `layer` already resolved above (via findLayer) instead of
      // findRaster, which silently no-op'd for non-raster kinds.
      const newId = crypto.randomUUID();
      ctx.emit(new DuplicateLayerCommand(layer.id, newId));
      const dup = findLayer(ctx.doc.layers, newId);
      if (!dup) return;
      this.lastLayerId = newId;
      this.active = {
        layerId: newId,
        startX: dup.x,
        startY: dup.y,
        grabDocX: p.x,
        grabDocY: p.y,
        lastDx: 0,
        lastDy: 0,
        shiftConstrain: s.shiftKey,
        lockedAxis: null,
        selectionAtFloat: null,
        extraStarts: [],
      };
      return;
    }

    const sel = ctx.doc.selection;

    // Float path: only when the active layer is raster AND there is an active
    // selection AND the drag starts INSIDE the selection. Dragging outside the
    // selection moves the whole layer (Photoshop behaviour for the Move tool
    // with a selection active). Non-raster layers are never floated.
    if (
      layer.kind === "raster" &&
      sel && !sel.isEmpty() &&
      selectionContainsPoint(sel, p.x, p.y, ctx.doc.width, ctx.doc.height)
    ) {
      const rasterLayer = layer as RasterLayer;
      const w = rasterLayer.pixels.width;
      const h = rasterLayer.pixels.height;
      const srcData = rasterLayer.pixels.data;
      const selMask = sel.mask;

      // Snapshot source pixels BEFORE mutation (for undo restore)
      const pixelsBefore = new Uint8ClampedArray(srcData);

      const newPixels = new PixelBuffer(w, h, "rgba8");
      const dstData = newPixels.data;

      // Lift selected pixels into the float layer and zero them in the source
      for (let py = 0; py < h; py++) {
        for (let px = 0; px < w; px++) {
          const idx = (py * w + px) * 4;
          const maskVal = selMask[py * w + px] ?? 0;
          if (maskVal > 0) {
            const ma = maskVal / 255;
            dstData[idx] = srcData[idx]!;
            dstData[idx + 1] = srcData[idx + 1]!;
            dstData[idx + 2] = srcData[idx + 2]!;
            dstData[idx + 3] = Math.round(srcData[idx + 3]! * ma);
            srcData[idx] = 0;
            srcData[idx + 1] = 0;
            srcData[idx + 2] = 0;
            srcData[idx + 3] = 0;
          }
        }
      }
      rasterLayer.pixels.markDirty();

      // Snapshot source pixels AFTER mutation (for redo re-zero)
      const pixelsAfter = new Uint8ClampedArray(srcData);

      const floatingLayer: RasterLayer = {
        id: crypto.randomUUID(),
        kind: "raster",
        name: `Layer ${ctx.doc.layers.length + 1}`,
        visible: true,
        opacity: 1,
        fillOpacity: 1,
        blendMode: "normal",
        x: 0,
        y: 0,
        lockTransparency: false,
        lockImage: false,
        lockPosition: false,
        lockAll: false,
        clippedToBelow: false,
        pixels: newPixels,
      };

      // Emit a single undoable command covering both the source-zero and the new layer.
      // The selection is kept on the document so the marching-ants are visible
      // during the drag; on pointer-up we emit MoveLayerAndSelectionCommand which
      // coalesces with this entry so float+move is ONE undo step. extraStarts'
      // ORIGINAL positions travel with the float command too (not just the
      // follow-up move) so that command's inverse — the one History keeps when
      // it coalesces with the pointer-up move — can snap them back on undo
      // regardless of how far they were dragged (i4/i56).
      const extraOriginal: ExtraLayerOriginal[] = extraStarts.map((e) => ({
        id: e.layerId,
        x: e.startX,
        y: e.startY,
      }));
      ctx.emit(new FloatSelectionCommand(rasterLayer.id, pixelsBefore, pixelsAfter, floatingLayer, extraOriginal));
      this.lastLayerId = floatingLayer.id;
      this.active = {
        layerId: floatingLayer.id,
        startX: 0,
        startY: 0,
        grabDocX: p.x,
        grabDocY: p.y,
        lastDx: 0,
        lastDy: 0,
        shiftConstrain: s.shiftKey,
        lockedAxis: null,
        // Capture the selection as it was at float time so we can translate it
        // on pointer-up to march with the moved pixels.
        selectionAtFloat: sel,
        extraStarts,
      };
      return;
    }

    // Outside-selection (or no selection, or non-raster layer): move the whole layer;
    // selection stays put.
    this.lastLayerId = layer.id;
    this.active = {
      layerId: layer.id,
      startX: layer.x,
      startY: layer.y,
      grabDocX: p.x,
      grabDocY: p.y,
      lastDx: 0,
      lastDy: 0,
      shiftConstrain: s.shiftKey,
      lockedAxis: null,
      selectionAtFloat: null,
      extraStarts,
    };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    let dx = p.x - this.active.grabDocX;
    let dy = p.y - this.active.grabDocY;
    // Shift held: constrain to horizontal or vertical axis (Photoshop behaviour).
    // The axis is decided ONCE — the first time Shift is detected held during
    // this drag — from the dominant direction at that moment, then frozen for
    // the rest of the gesture. Re-evaluating it on every move (the previous
    // behaviour) let the locked axis silently flip mid-drag whenever the
    // cumulative dx/dy crossed the diagonal (i155).
    let lockDx = false;
    let lockDy = false;
    if (s.shiftKey || this.active.shiftConstrain) {
      if (this.active.lockedAxis === null) {
        this.active.lockedAxis = Math.abs(dx) >= Math.abs(dy) ? "y" : "x";
      }
      if (this.active.lockedAxis === "y") {
        dy = 0;
        lockDy = true;
      } else {
        dx = 0;
        lockDx = true;
      }
    } else {
      // Shift not engaged (and wasn't at drag start) — forget any stale axis
      // lock so a later Shift-hold in this same drag re-evaluates fresh.
      this.active.lockedAxis = null;
    }
    // Snap the candidate new position (layer origin) to guides/grid/other-layer
    // edges/document bounds (audit i26). A locked axis (shift-constrain) stays
    // pinned at zero even if a target happens to sit nearby, so snapping never
    // fights the axis constraint.
    const snapped = ctx.snapPoint?.(this.active.startX + dx, this.active.startY + dy);
    if (snapped) {
      dx = lockDx ? 0 : snapped.x - this.active.startX;
      dy = lockDy ? 0 : snapped.y - this.active.startY;
    }
    // Round the resulting layer position to whole document pixels so drags at
    // fractional-zoom levels (e.g. 33%, 67%) don't leave the layer sitting at
    // a sub-pixel offset — Photoshop always keeps moved layers pixel-aligned
    // (i52). Rounding the ABSOLUTE target (not the delta) guarantees
    // startX + dx lands exactly on an integer regardless of startX's own
    // fractional part.
    dx = Math.round(this.active.startX + dx) - this.active.startX;
    dy = Math.round(this.active.startY + dy) - this.active.startY;
    if (dx === this.active.lastDx && dy === this.active.lastDy) return;
    this.active.lastDx = dx;
    this.active.lastDy = dy;
    ctx.emitTransient(
      new MoveLayerCommand(
        this.active.layerId,
        this.active.startX,
        this.active.startY,
        this.active.startX + dx,
        this.active.startY + dy,
      ),
    );
    // Live-preview the other selected/linked layers moving by the same delta
    // (transient — never recorded in history, matching the primary preview).
    for (const extra of this.active.extraStarts) {
      ctx.emitTransient(
        new MoveLayerCommand(
          extra.layerId,
          extra.startX,
          extra.startY,
          extra.startX + dx,
          extra.startY + dy,
        ),
      );
    }
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    const { layerId, startX, startY, lastDx, lastDy, selectionAtFloat, extraStarts } = this.active;
    this.active = null;
    if (lastDx === 0 && lastDy === 0) return;

    const toX = startX + lastDx;
    const toY = startY + lastDy;

    if (selectionAtFloat) {
      // Float-move commit: translate the selection with the pixels so the
      // marching ants surround the moved region (Photoshop behaviour).
      // This command coalesces with the FloatSelectionCommand emitted at
      // pointer-down → ONE undo step for the whole float+move gesture.
      // Extra selected/linked layers (if any) are folded into this same
      // command so they commit — and undo — as part of that one step.
      const selAfter = selectionAtFloat.translate(Math.round(lastDx), Math.round(lastDy));
      const extraMoves: ExtraLayerMove[] = extraStarts.map((e) => ({
        id: e.layerId,
        fromX: e.startX,
        fromY: e.startY,
        toX: e.startX + lastDx,
        toY: e.startY + lastDy,
      }));
      ctx.emit(
        new MoveLayerAndSelectionCommand(
          layerId,
          startX,
          startY,
          toX,
          toY,
          selectionAtFloat,
          selAfter,
          extraMoves,
        ),
      );
    } else if (extraStarts.length === 0) {
      // Single layer moving — unchanged behaviour (kind "move-layer").
      ctx.emit(new MoveLayerCommand(layerId, startX, startY, toX, toY));
    } else {
      // Multiple layers moving together (i4/i56): one atomic command so a
      // single undo reverts the whole multi-layer move.
      const moves: LayerMoveEntry[] = [
        { layerId, fromX: startX, fromY: startY, toX, toY },
        ...extraStarts.map((e) => ({
          layerId: e.layerId,
          fromX: e.startX,
          fromY: e.startY,
          toX: e.startX + lastDx,
          toY: e.startY + lastDy,
        })),
      ];
      ctx.emit(new MoveLayersCommand(moves));
    }
  }
}

/**
 * Return true when a doc-space point (possibly fractional / out-of-bounds)
 * is meaningfully inside the selection. We sample a 2×2 neighbourhood to
 * handle anti-aliased edges where the exact pixel may be < 128.
 *
 * Uses raw mask access so this works with both real Selection objects and
 * lightweight test stubs that only expose `mask`.
 */
function selectionContainsPoint(
  sel: Selection,
  x: number,
  y: number,
  docW: number,
  docH: number,
): boolean {
  const mask = sel.mask;
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let maxVal = 0;
  for (let ddy = 0; ddy <= 1; ddy++) {
    for (let ddx = 0; ddx <= 1; ddx++) {
      const px = xi + ddx;
      const py = yi + ddy;
      if (px < 0 || py < 0 || px >= docW || py >= docH) continue;
      const v = mask[py * docW + px] ?? 0;
      if (v > maxVal) maxVal = v;
    }
  }
  return maxVal > 128;
}

/**
 * i55 — Auto-Select "Group" mode: resolve `targetId` to the id of the
 * outermost TOP-LEVEL entry (a direct child of `layers`) whose subtree
 * contains it. If `targetId` is itself top-level (not nested in any group),
 * its own id is returned unchanged — Photoshop's Auto-Select: Group just
 * selects the layer directly when it isn't inside a group. Returns null only
 * if `targetId` isn't found anywhere in the tree.
 */
function findTopLevelAncestorId(layers: readonly Layer[], targetId: string): string | null {
  for (const top of layers) {
    if (top.id === targetId) return top.id;
    if (top.kind === "group" && subtreeContainsId(top, targetId)) return top.id;
  }
  return null;
}

function subtreeContainsId(layer: Layer, targetId: string): boolean {
  if (layer.id === targetId) return true;
  if (layer.kind === "group") {
    for (const child of layer.children) {
      if (subtreeContainsId(child, targetId)) return true;
    }
  }
  return false;
}

function findLayerAtPosition(doc: Document, docX: number, docY: number): RasterLayer | null {
  for (let i = doc.layers.length - 1; i >= 0; i--) {
    const hit = findRasterAt(doc.layers[i]!, docX, docY);
    if (hit) return hit;
  }
  return null;
}

function findRasterAt(layer: Layer, docX: number, docY: number): RasterLayer | null {
  if (layer.kind === "group") {
    for (let i = layer.children.length - 1; i >= 0; i--) {
      const hit = findRasterAt(layer.children[i]!, docX, docY);
      if (hit) return hit;
    }
    return null;
  }
  if (layer.kind !== "raster") return null;
  if (!layer.visible) return null;
  const lx = docX - layer.x;
  const ly = docY - layer.y;
  if (lx < 0 || ly < 0 || lx >= layer.pixels.width || ly >= layer.pixels.height) return null;
  const idx = (ly * layer.pixels.width + lx) * 4;
  if (layer.pixels.data[idx + 3] === 0) return null;
  return layer;
}
